import { setViewportSearchParamAndRemoveMapbox, useKey } from '@core/logic';
import { MapControls } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import { jsonToVector } from '@viewer3D/helper';
import type { BoundingBoxWithHeight } from '@viewer3D/types';
import { debounce } from 'lodash';
import { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import type { Event, OrthographicCamera } from 'three';
import type { MapControls as MapControlsType } from 'three-stdlib';
import { connectComponent } from './connect';
import { boundsMatch, updateControlsCameraPosition } from './helper';
import { useMapboxPosition } from './hooks';
import type { MergedProps } from './types';

const CameraControlsBase: FC<MergedProps> = ({
  cameraViewport,
  goTo,
  setCameraViewport,
  setGoto,
  setMapControls,
}) => {
  const camera = useThree((state) => state.camera);
  const mapRef = useRef<MapControlsType>(null);
  const addPressed = useKey('a');
  const selectionAreaPressed = useKey('Shift');
  const mapboxPosition = useMapboxPosition();
  const appliedMapboxRef = useRef<string | null>(null);

  const cameraViewportRef = useRef(cameraViewport);
  cameraViewportRef.current = cameraViewport;

  const debouncedUpdateViewport = useMemo(
    () =>
      debounce((zoom: number, x: number, y: number, z: number) => {
        const viewer3d = document.getElementById('viewer3d');
        if (!viewer3d) return;
        const maxResolution = Math.max(viewer3d.offsetWidth, viewer3d.offsetHeight);
        let scale = ((1 / zoom) * maxResolution) / 2;
        const viewportPadding = scale / 3; // Additional space around the visible viewport (to account edges/tilted camera and reduce rerenders)
        scale += viewportPadding;
        const nextCameraViewport: BoundingBoxWithHeight = {
          minX: Math.round(x - scale),
          maxX: Math.round(x + scale),
          minY: Math.round(y - scale),
          maxY: Math.round(y + scale),
          z: Math.ceil(z * 10) / 10,
        };
        const currentViewport = cameraViewportRef.current;
        if (
          !currentViewport ||
          !boundsMatch(currentViewport, nextCameraViewport, viewportPadding / 2)
        ) {
          setCameraViewport(nextCameraViewport);
        }
      }, 500),
    [setCameraViewport],
  );

  useEffect(() => {
    return () => {
      debouncedUpdateViewport.cancel();
    };
  }, [debouncedUpdateViewport]);

  const updateCameraBounds = useCallback(
    (event?: Event) => {
      if (!event) return;
      const { target, object } = event.target as MapControlsType;
      const { zoom } = object as OrthographicCamera;
      const { x, y, z } = target;
      debouncedUpdateViewport(zoom, x, y, z);
    },
    [debouncedUpdateViewport],
  );

  useEffect(() => {
    if (!mapRef.current || !setMapControls) return;
    setMapControls(mapRef.current);
    Reflect.set(camera.userData, 'mapControl', mapRef.current);
    return () => {
      Reflect.set(camera.userData, 'mapControl', undefined);
      setMapControls(undefined);
    };
  }, [camera, setMapControls]);

  useEffect(() => {
    const { current: map } = mapRef;
    if (!map || !goTo) return;
    updateControlsCameraPosition(map, jsonToVector(goTo));
    updateCameraBounds({ type: 'end', target: map });
    setGoto();
  }, [setGoto, goTo, updateCameraBounds]);

  useEffect(() => {
    const { current: map } = mapRef;
    if (!map || !mapboxPosition || appliedMapboxRef.current === mapboxPosition.raw) return;

    appliedMapboxRef.current = mapboxPosition.raw;
    updateControlsCameraPosition(map, mapboxPosition.vector, mapboxPosition.zoom);
    updateCameraBounds({ type: 'end', target: map });
    setGoto();

    const viewer3d = document.getElementById('viewer3d');
    const width = viewer3d?.offsetWidth || 1920;
    const height = viewer3d?.offsetHeight || 1080;
    const maxResolution = Math.max(width, height);
    const { zoom } = map.object as OrthographicCamera;
    const { x, y, z } = map.target;
    let scale = ((1 / zoom) * maxResolution) / 2;
    const viewportPadding = scale / 3;
    scale += viewportPadding;
    const nextCameraViewport: BoundingBoxWithHeight = {
      minX: Math.round(x - scale),
      maxX: Math.round(x + scale),
      minY: Math.round(y - scale),
      maxY: Math.round(y + scale),
      z: Math.ceil(z * 10) / 10,
    };

    setViewportSearchParamAndRemoveMapbox(nextCameraViewport);
  }, [mapboxPosition, setGoto, updateCameraBounds]);

  /**
   * Unfortunately we need to add/remove the listener manually because component has a bug
   * <MapControls onEnd={**THIS IS LOOSING CONTEXT & NOT WORKING CORRECTLY**}/>
   */
  useEffect(() => {
    const { current: map } = mapRef;
    if (!map) return;
    map.addEventListener('end', updateCameraBounds);
    return () => {
      map.removeEventListener('end', updateCameraBounds);
    };
  }, [updateCameraBounds]);

  return (
    <MapControls
      ref={mapRef}
      enableDamping={false}
      enablePan={!addPressed && !selectionAreaPressed}
      enableRotate={!addPressed && !selectionAreaPressed}
    />
  );
};

export const CameraControls = connectComponent(CameraControlsBase);
